import { useState, useEffect, useContext, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { GlobalContext } from '../context/global.context'
import useFavApi from '../apis/useFavApi'
import { routeMap } from '../route'
import utils from '../utils/utils'
import useFirebaseTrace from '../hooks/useFirebaseTrace'
import { splitLanguage } from '../utils/localStorageService'
import { getFavouritesLocal, setFavouritesLocal } from '../utils/sessionStorageService'

// import { useWhatChanged } from '@simbathesailor/use-what-changed';
// USAGE: useWhatChanged([a, b, c, d], 'a, b, c, d'); // debugs the below useEffect

/** Custom hook for to handle favourites rail */
const useFavourites = ({ sysId, query, pageType, pageName }) => {
  const firebaseTrace = useFirebaseTrace()
  const { getFavourites } = useFavApi()
  const [railInfo, updateRailInfo] = useState({})
  const [railItemsInfo, updateRailItemsInfo] = useState(getPlaceholders())
  const [isNoData, setIsNoData] = useState(false)
  const [identifierExt, setIdentifierExt] = useState(null)
  const { isAuthenticated, appLanguage, isShowSplash, favouritesCallState, userToken } = useContext(GlobalContext)
  const isScrollPaginationRef = useRef(false)
  const { pageComponent = {} } = useSelector(store => store.navigation)
  const { appConfig } = useSelector(store => store.appConfigReducers)
  const { pageComponents = {} } = pageComponent
  const pComponent = pageComponents[sysId]
  const pComponentRef = useRef({})
  const pageNameRef = useRef('')
  const traceRef = useRef(null)

  /** Fetches favourites data */
  const fetchFavourites = useCallback((fnParams) => {
    let {
      currentPageNumber = 1, railItems = [], maxItems = null,
      onPageResponse = () => { }, baseUrl = '', appLanguage = ''
    } = fnParams || {}
    const params = {
      page: currentPageNumber,
      language: splitLanguage(appLanguage)
    }
    return getFavourites(baseUrl, params).then((res = {}) => {
      if (res.content) {
        const content = res.content || []
        railItems = [...railItems, ...content]
        onPageResponse(railItems)
      } else {
        return Promise.reject(railItems)
      }
      if (currentPageNumber + 1 <= (res.totalPages || 0) && (maxItems === null || railItems < maxItems)) {
        return fetchFavourites({
          ...fnParams,
          railItems,
          currentPageNumber: currentPageNumber + 1
        })
      } else {
        return Promise.resolve(railItems)
      }
    })
  }, [getFavourites])

  useEffect(() => {
    pComponentRef.current = pComponent
  }, [pComponent])

  useEffect(() => {
    pageNameRef.current = pageName
  }, [pageName])

  useEffect(() => {
    /**
     * Process rail configuration.
     */
    if (favouritesCallState !== 'ENDED') return
    if (!isAuthenticated || !userToken) return
    if (isShowSplash) return
    if (!appConfig.baseUrl) return
    if (!pComponentRef.current) return
    if (!sysId) return

    const _query = utils.parse(query)
    const { config = {}, componentStyle = [], theme = {} } = pComponentRef.current || {}
    const { data, params, type, identifierExt, title } = config || {}
    const maxItems = _query.viewall ? null : (config || {}).maxItems

    if (config.identifierExt !== 'favorites' && config.identifierExt !== 'Favourites Page Component') return
    setIdentifierExt(config.identifierExt)

    const trace = firebaseTrace('listing_page_load', {
      pageTitle: 'favourites'
    })
    traceRef.current = trace

    let style = componentStyle.map((style) => {
      return { ...style }
    })
    if (style.length > 0 && pageType === 'listing') {
      style = style.map((style) => {
        style.type = 'grid'
        if (!_query.viewall) {
          style.showComponentTitle = true
          style.showItemTitle = true
          style.itemSize = 'medium'
          style.itemEdgeRadius = 0.25
          style.itemOrientation = 0.667
        }
        if (style.itemOrientation) {
          if (style.itemOrientation > 1) {
            style.maxItemTitleLines = 2 // landscape
          } else {
            style.maxItemTitleLines = 3 // portrait
          }
        }
        return { ...style }
      })
    }
    isScrollPaginationRef.current = isScrollPaginate(style)
    const viewAllLink = `${routeMap.listing}?sysId=${sysId}&viewall=true&pageName=${pageNameRef.current}`
    updateRailInfo({
      ...config,
      title,
      data,
      identifierExt,
      params,
      type,
      maxItems,
      style,
      viewAllLink,
      sysId,
      isRailItemPlayBtn: false,
      componentTheme: theme,
      favouritesPage: true,
      pageName: pageNameRef.current || _query.pageName
    })
    updateRailItemsInfo(getPlaceholders())
  }, [
    appConfig.baseUrl,
    isAuthenticated,
    isShowSplash,
    pageType,
    query,
    sysId,
    favouritesCallState,
    userToken,
    firebaseTrace
  ])

  useEffect(() => {
    /**
     * Fetch and update rail data.
     */
    if (favouritesCallState !== 'ENDED') return
    if (!isAuthenticated || !userToken) return
    if (isShowSplash) return
    if (!appConfig.baseUrl) return
    if (!pComponentRef.current) return
    if (identifierExt !== 'favorites' && identifierExt !== 'Favourites Page Component') return
    if (!sysId) return

    let railItemData = getFavouritesLocal() || []
    railItemData = railItemData.filter(Boolean).filter(item => !item.isPlaceholder)
    if (railItemData.length === 0) {
      updateRailItemsInfo([])
      setIsNoData(true)
    } else updateRailItemsInfo(railItemData)

    if (traceRef.current) {
      traceRef.current.stop()
      traceRef.current = null
    }

    const _query = utils.parse(query)
    const { config = {} } = pComponentRef.current || {}
    const maxItems = _query.viewall ? null : (config || {}).maxItems

    const onPageResponse = (railItems = []) => {
      setFavouritesLocal(railItems)
    }

    fetchFavourites({
      currentPageNumber: 1,
      railItems: [],
      maxItems,
      onPageResponse,
      config,
      baseUrl: appConfig.baseUrl,
      appLanguage
    }).then((railItems = []) => {
      setFavouritesLocal(railItems)
    }).catch((error) => {
      console.error('ERROR: Favourites', error)
    })
  }, [
    appConfig.baseUrl,
    appLanguage,
    identifierExt,
    isAuthenticated,
    query,
    sysId,
    isShowSplash,
    fetchFavourites,
    favouritesCallState,
    userToken
  ])

  return { railInfo, railItemsInfo, isNoData }
}

const getPlaceholders = () => {
  /**
   * @return Rail placeholders data.
   */
  const items = []
  for (let i = 0; i < 3; i++) items.push({ isPlaceholder: true })
  return items
}

const isScrollPaginate = (style) => {
  /**
   * @return type of scroll.
   */
  return !(utils.isNotNull(style, 0, 'type') && style[0].type === 'grid')
}

export default useFavourites
